// 🔧 getting_started.js
// ✅ Purpose: Public-facing manager guide for getting started with Let's Go Vote
// ✅ Features:
// - Explains the first setup steps for a new event manager
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Includes step-by-step sections, tips, and calls to action
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function GettingStartedGuide() {
  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Getting Started Guide | Let's Go Vote"
        description="Learn how to set up your first Let's Go Vote event, including event details, voting setup, entries, QR codes, and event day preparation."
        path="/guides/getting-started"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
          {
            name: "Getting Started",
            path: "/guides/getting-started",
          },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🚀 Getting Started</h1>
          <p className="lead text-muted">
            Learn the basic steps to set up your first event with Let&apos;s Go Vote.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">👋 Welcome, event manager</h5>
            <p className="mb-0">
              Let&apos;s Go Vote helps you manage voting, entries, QR codes,
              sponsors, volunteers, auctions, leaderboards, and event-day results
              from one event website.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 1: Request or create your event</h3>
          <p>
            Start by requesting access for your event. Once approved, your event
            will appear on your dashboard and you can begin configuring the public
            event page, voting rules, categories, entries, and optional modules.
          </p>

          <Link to="/request_event" className="btn btn-outline-primary">
            📬 Request an Event
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 2: Confirm your event details</h3>
          <p>
            Add your event name, dates, description, public event content, logo,
            and event images. These details help attendees understand what your
            event is about and when participation is available.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 3: Choose your voting type</h3>
          <p>
            Let&apos;s Go Vote supports multiple voting styles, including
            People&apos;s Choice Voting, Ranked Choice Voting, Judged Points, and
            Scorecard-style judging. Choose the option that best matches your event.
          </p>

          <Link to="/guides/configure-voting" className="btn btn-sm btn-outline-primary">
            Read the Voting Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 4: Add categories and entries</h3>
          <p>
            Categories organize your event into voting groups. Entries are the
            people, cars, pets, artwork, teams, candidates, or items that attendees
            will vote on.
          </p>

          <Link to="/guides/categories-and-entries" className="btn btn-sm btn-outline-primary">
            Read the Categories & Entries Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 5: Print or share QR codes</h3>
          <p>
            QR codes make event-day participation simple. Attendees can scan a QR
            code to open the event, view an entry, place a bid, or submit a vote.
          </p>

          <Link to="/guides/qr-codes" className="btn btn-sm btn-outline-primary">
            Read the QR Code Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 6: Test before event day</h3>
          <p>
            Before your event begins, test your public event page, voting flow,
            QR codes, categories, entries, and leaderboard settings. Testing early
            helps prevent confusion during the live event.
          </p>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Next recommended guide</h5>
            <p className="mb-3">
              After your event is created, the next major decision is how voting
              should work.
            </p>

            <Link to="/guides/configure-voting" className="btn btn-primary">
              Continue to Configure Voting
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default GettingStartedGuide;